import { NUMBER } from '@constants/common/number';
import { RELATIONSHIP_TYPES } from '@constants/common/relationShipTypes';
import {
    LAST_DAY_OF_YEAR,
    TXN_REASON,
    TXN_REASONS_TEXT,
    TXN_TYPES,
} from '@constants/enrollmentList/enrollmentList.constant';
import { TEXTAREA_MAX_LENGTH } from '@constants/enrollments/enrollments.constants';
import { IMember } from '../../../custom-grouping/types/customGroupingTypes';
import { IEnrollee } from './types';

export const changeFieldValue = (setValue, name: string, value: any) => {
    setValue(name, value, {
        shouldDirty: true,
        shouldValidate: true,
    });
};

export const aptcAmountFieldValue = (setValue, name: string, value: any) => {
    setValue(name, value, {
        shouldDirty: true,
    });
};

/**
 * Updates the value of a currency field in the enrollment premium DTO list.
 *
 * @param setValue - The function to set the value of the field.
 * @param field - The field name to be updated.
 * @param premium - An object containing the month information.
 * @param value - The new value to be set, as a string.
 *
 * The function ensures that the value is a valid currency format before updating.
 * It will not update the value if it ends with '.', '.0', or '.00'.
 *
 * @example
 * // Assuming setValue is a function that updates the state,
 * // field is 'premiumAmount', premium is { month: 3 }, and value is '123.45'
 * currencyChange(setValue, 'premiumAmount', { month: 3 }, '123.45');
 * // This will call setValue with the path 'enrollmentPremiumDtoList.2.premiumAmount'
 * // and the value 123.45
 */

export const currencyChange = (setValue, field, premium, value) => {
    const isPartialDecimal = value.endsWith('.') || value.endsWith('.0') || value.endsWith('.00');

    if (isPartialDecimal) {
        // Keep the user's input as-is during typing
        setValue(`enrollmentPremiumDtoList.${premium.month - 1}.${field}`, value || 0, {
            shouldDirty: true,
        });
    } else if (!isNaN(Number(value))) {
        // Convert only when it's a clean, valid number
        const parsed = Number(parseFloat(value).toFixed(2));
        setValue(`enrollmentPremiumDtoList.${premium.month - 1}.${field}`, parsed >= 0 ? parsed : null, {
            shouldDirty: true,
            shouldValidate: true,
        });
    } else {
        // Optional fallback to empty string or 0
        setValue(`enrollmentPremiumDtoList.${premium.month - 1}.${field}`, '', {
            shouldDirty: true,
        });
    }
};

export const setCustomError = (
    fn: (field: string, error: { type: string; message: string }) => void,
    field: string,
    error: string,
    type: string = 'custom',
) => {
    fn(field, {
        type: type,
        message: error,
    });
};

export const getOptionsByTxnType = (txnType: string, relationshipToSubscriber: string) => {
    let options: { text: string; value: string }[] = [];

    if (txnType == TXN_TYPES.Add) {
        options = [...options, { text: TXN_REASONS_TEXT.MEMBER_BENEFIT_SELECTION, value: TXN_REASON.EC }];
    }

    if (txnType == TXN_TYPES.Change) {
        options = [...options, { text: TXN_REASONS_TEXT.BENEFIT_SELECTION, value: TXN_REASON.TwentyNine }];
    }

    if (txnType != TXN_TYPES.Add) {
        options = [...options, { text: TXN_REASONS_TEXT.NO_REASON_GIVEN, value: TXN_REASON.AI }];
    }

    if (
        txnType != TXN_TYPES.Add &&
        txnType != TXN_TYPES.Change &&
        relationshipToSubscriber.toUpperCase() === RELATIONSHIP_TYPES.SELF.value
    ) {
        options = [...options, { text: TXN_REASONS_TEXT.NON_PAYMENT, value: TXN_REASON.FiftyNine }];
    }

    if (txnType != TXN_TYPES.Add && txnType != TXN_TYPES.Change) {
        options = [
            ...options,
            { text: TXN_REASONS_TEXT.VOLUNTARY_WITHDRAWAL, value: TXN_REASON.Fourteen },
            { text: TXN_REASONS_TEXT.DEATH, value: TXN_REASON.ZeroThree },
        ];
    }

    return options;
};

export const validateTextArea = (value: string) => {
    if (value.length > TEXTAREA_MAX_LENGTH) {
        value = value.slice(0, TEXTAREA_MAX_LENGTH);
    }

    return value;
};

export function getDuplicateMemberStrings(duplicateMemberMap: { [key: string]: IMember }): string[] {
    const duplicateMemberStrings: string[] = [];
    for (const key in duplicateMemberMap) {
        if (Object.prototype.hasOwnProperty.call(duplicateMemberMap, key)) {
            const value = duplicateMemberMap[key];
            let memberString = `${key}, ${value.houseHoldCaseId}, ${value.firstName} ${value.lastName}`;
            const enrollmentIds: string[] = [];
            if (value.healthEnrollmentId != null) {
                enrollmentIds.push(value.healthEnrollmentId);
            }
            if (value.dentalEnrollmentId != null) {
                enrollmentIds.push(value.dentalEnrollmentId);
            }
            memberString += enrollmentIds.map(id => `, ${id}`).join('');
            duplicateMemberStrings.push(memberString);
        }
    }
    return duplicateMemberStrings;
}

export const getMemberOrder = relationshipToSubscriber => {
    let order = 4;
    switch (relationshipToSubscriber.toUpperCase()) {
        case RELATIONSHIP_TYPES.SELF.value:
            order = 1;
            break;
        case RELATIONSHIP_TYPES.SPOUSE.value:
            order = 2;
            break;
        case RELATIONSHIP_TYPES.CHILD.value:
            order = 3;
            break;
        default:
            order = 4;
    }

    return order;
};

export const getEnrolleeOrderList = enrolleeDataDtoList => {
    if (!!enrolleeDataDtoList) {
        let enrolleeData = enrolleeDataDtoList?.map(enrollee => ({
            ...enrollee,
            order: getMemberOrder(enrollee?.relationshipToSubscriber),
        }));
        enrolleeData.sort((a, b) => a.order - b.order);

        return enrolleeData.map((enrollee: IEnrollee, index: number) => ({
            ...enrollee,
            index,
        }));
    } else {
        return enrolleeDataDtoList;
    }
};

export type EnrolleeCountSummary = {
    selfCount: number;
    spouseCount: number;
    dependentCount: number;
};

/**
 * Counts unique primary/spouse/dependent enrollees by memberId.
 * Multiple coverage spans for the same member must not inflate the summary.
 */
export const getUniqueEnrolleeCounts = (
    enrolleeDataDtoList: Pick<IEnrollee, 'memberId' | 'relationshipToSubscriber'>[] | null | undefined,
): EnrolleeCountSummary => {
    const counts: EnrolleeCountSummary = { selfCount: 0, spouseCount: 0, dependentCount: 0 };
    if (!enrolleeDataDtoList?.length) {
        return counts;
    }

    const seenMemberIds = new Set<string>();

    for (const enrollee of enrolleeDataDtoList) {
        const memberId = enrollee.memberId != null ? String(enrollee.memberId).trim() : '';
        if (memberId && seenMemberIds.has(memberId)) {
            continue;
        }
        if (memberId) {
            seenMemberIds.add(memberId);
        }

        const relationship = (enrollee.relationshipToSubscriber || '').toUpperCase();
        if (relationship === RELATIONSHIP_TYPES.SELF.value) {
            counts.selfCount++;
        } else if (relationship === RELATIONSHIP_TYPES.SPOUSE.value) {
            counts.spouseCount++;
        } else if (relationship) {
            counts.dependentCount++;
        }
    }

    return counts;
};

export const isYearEndDate = (dateStr: string): boolean => {
    if (!dateStr || dateStr.length < NUMBER.FIVE) return false;
    return dateStr.substring(NUMBER.ZERO, NUMBER.FIVE) === LAST_DAY_OF_YEAR;
};

type EffectiveMonthRange = {
    enrollmentBenefitEffectiveStartMonth?: number | null;
    enrollmentBenefitEffectiveEndMonth?: number | null;
};

// When the original effective range is unknown (missing bounds), treat the month as
// inside the range so callers don't accidentally gate-off behavior they previously had.
export const isMonthOutsideOriginalEffectiveRange = (
    month: number,
    enrollment: EffectiveMonthRange | null | undefined,
): boolean => {
    const start = enrollment?.enrollmentBenefitEffectiveStartMonth;
    const end = enrollment?.enrollmentBenefitEffectiveEndMonth;
    if (start == null || end == null) {
        return false;
    }
    return month < start || month > end;
};
